import { useCallback } from "react";
import { useFaust, type CompileOptions } from "../contexts/FaustContext";

/**
 * Hook for reading/writing compile options with auto-persistence.
 * Options are stored in a ref (not state) since they change frequently
 * and don't always need re-renders.
 */
export function useCompileOptions() {
    const { compileOptionsRef, saveCompileOptions } = useFaust();

    const getOptions = useCallback(() => compileOptionsRef.current, [compileOptionsRef]);

    const setOption = useCallback(
        <K extends keyof CompileOptions>(key: K, value: CompileOptions[K]) => {
            compileOptionsRef.current[key] = value;
            saveCompileOptions();
        },
        [compileOptionsRef, saveCompileOptions]
    );

    const setOptions = useCallback(
        (updates: Partial<CompileOptions>) => {
            Object.assign(compileOptionsRef.current, updates);
            saveCompileOptions();
        },
        [compileOptionsRef, saveCompileOptions]
    );

    return { getOptions, setOption, setOptions, optionsRef: compileOptionsRef };
}
